import { getErrorMessage } from "@/core/api/errors";
import { dayjs, formatDateAndTime } from "@/core/utils/date";
import { MAX_REPORT_SPAN_DAYS, RUN_STATUS } from "./constants";
import type {
  EnrichedProfitabilityPeriod,
  EnrichedProfitabilityRun,
  ProfitabilityPeriod,
  ProfitabilityRun,
  ReportDates,
  RunSummaryFigure,
} from "./types";

export const isActiveRun = (run: ProfitabilityRun) =>
  run.status === RUN_STATUS.pending || run.status === RUN_STATUS.running;

const isIsoDate = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  dayjs(value, "YYYY-MM-DD", true).isValid();

export const toIsoDate = (date: Date | null) =>
  date ? dayjs(date).format("YYYY-MM-DD") : "";

/** Days a range covers, counting both ends, the way the API counts them. */
const spanInDays = ({ start_date, end_date }: ReportDates) =>
  dayjs(end_date).diff(dayjs(start_date), "day") + 1;

export const dateRangeError = (dates: ReportDates) => {
  const { start_date, end_date } = dates;
  if (!isIsoDate(start_date) || !isIsoDate(end_date))
    return "Enter a start and end date.";
  if (start_date > end_date) return "The start date has to come first.";
  // Checked here as well as on the server, so a range that cannot work is said
  // so before the request rather than after it.
  const span = spanInDays(dates);
  if (span > MAX_REPORT_SPAN_DAYS)
    return `A report can cover at most ${MAX_REPORT_SPAN_DAYS} days. This range is ${span}.`;
  return null;
};

/**
 * "Sep 01 - Sep 30, 2026", collapsing a single-day range to one date. The start
 * keeps its own year when the range crosses one, or "Dec 15 - Jan 15, 2026"
 * reads as starting in December 2026.
 */
export const formatReportPeriod = ({ start_date, end_date }: ReportDates) => {
  const from = dayjs(start_date);
  const to = dayjs(end_date);
  if (start_date === end_date) return to.format("MMM DD, YYYY");

  const sameYear = from.year() === to.year();
  return `${from.format(sameYear ? "MMM DD" : "MMM DD, YYYY")} - ${to.format("MMM DD, YYYY")}`;
};

/** "30 days" — the span an owner asked for, not the time it took to build. */
export const formatReportSpan = (dates: ReportDates) => {
  const days = spanInDays(dates);
  return days === 1 ? "1 day" : `${days} days`;
};

/**
 * A headline figure the report's script tagged, formatted by the type it
 * declared. The script chooses which figures exist, so this must not assume any
 * particular one.
 */
export const formatSummaryValue = ({ value, type }: RunSummaryFigure) => {
  if (typeof value !== "number") return String(value ?? "");
  if (type === "percent")
    return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  if (type === "currency")
    return value.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  return value.toLocaleString();
};

/** Whatever the API said went wrong, in one line the owner can read. */
export const reportError = (error: unknown) => getErrorMessage(error);

export const reportPath = (businessId: string | null, runId?: string) =>
  `/businesses/${businessId}/profitability_report_runs${runId ? `/${runId}` : ""}`;

export const workbookPath = (businessId: string, runId: string) =>
  `${reportPath(businessId, runId)}/workbook`;

/**
 * Chat root for one report, in the shape the shared explanation-chat transport
 * expects (POST here, plus `/threads` and `/stream`).
 */
export const reportChatPath = (businessId: string, runId: string) =>
  `${reportPath(businessId, runId)}/chat`;

export const downloadFilename = (run: ProfitabilityRun) =>
  `profitability-${run.start_date}-${run.end_date}.xlsx`;

/** Preserve the API's figure labels and order; add only display formatting. */
export const enrichProfitabilityRun = (
  run: ProfitabilityRun,
): EnrichedProfitabilityRun => {
  const span = formatReportSpan(run);
  const requestedAt = formatDateAndTime(run.created_at);
  const summary = run.summary.map((figure) => ({
    ...figure,
    displayValue: formatSummaryValue(figure),
    inlineLabel: figure.label.toLowerCase(),
  }));
  return {
    ...run,
    display: {
      period: formatReportPeriod(run),
      span,
      requestedAt,
      completedAt:
        run.status === RUN_STATUS.completed && run.completed_at
          ? formatDateAndTime(run.completed_at)
          : null,
      metadata: [
        `Requested by ${run.requester.full_name}`,
        requestedAt,
        span,
      ].join(" · "),
      workbookReady:
        run.status === RUN_STATUS.completed && run.workbook_available,
      summary,
      supportingFigures: summary
        .slice(1)
        .map((figure) => `${figure.displayValue} ${figure.inlineLabel}`)
        .join(" · "),
    },
  };
};

/** Keep the available report visible while describing a newer attempt. */
export const enrichProfitabilityPeriod = (
  period: ProfitabilityPeriod,
): EnrichedProfitabilityPeriod => {
  const run = enrichProfitabilityRun(period.report ?? period.latest_attempt);
  const attempt = period.latest_attempt;
  let latestAttemptMessage: string | null = null;
  if (attempt.id !== run.id) {
    switch (attempt.status) {
      case RUN_STATUS.failed:
        latestAttemptMessage = `Latest attempt failed. ${attempt.error?.message ?? "Try again."}`;
        break;
      case RUN_STATUS.pending:
        latestAttemptMessage = "New report queued";
        break;
      case RUN_STATUS.running:
        latestAttemptMessage = "New report running";
        break;
      case RUN_STATUS.completed:
        latestAttemptMessage = "Latest attempt has no workbook";
        break;
    }
  }
  return {
    ...run,
    display: { ...run.display, latestAttemptMessage },
  };
};
